import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FieldSelector from 'components/FieldSelector/FieldSelector';
import {
  getStartOfDayTimestamp,
  getBrowserTimeZone,
  getZonedCalendarDate,
  getZonedTimestamp,
} from 'helpers/dateTimeHelper';
import {
  seedGraceFor,
  effectiveHoursOfDay,
  graceUnitFor,
  isDayCoarse,
} from 'helpers/recurrenceLimits';
import CadenceEditor from './recurrence/CadenceEditor';
import TimeZoneEditor from './recurrence/TimeZoneEditor';
import FirstDueDateEditor from './recurrence/FirstDueDateEditor';
import EarlySubmissionEditor from './recurrence/EarlySubmissionEditor';
import SegmentedToggle from './recurrence/SegmentedToggle';
import { scopeToAssetFields } from './recurrence/FirstDueDateEditor';

var MODES = ['none', 'manual', 'schedule'];

// The inputs seedGraceFor/maxGraceFor read -- a partial touching any of these changes the grace
// unit, suggestion and/or ceiling, so it is the one signal handleRecurrencePartial needs to decide
// whether the grace value needs reconciling (review round 1, Important #3). The slot arrays count
// because the window follows the gap between occurrences, not the nominal interval.
var CADENCE_KEYS = ['frequency', 'interval', 'hoursOfDay', 'daysOfWeek', 'daysOfMonth'];

function touchesCadence(partial) {
  for (var i = 0; i < CADENCE_KEYS.length; i++) {
    if (Object.prototype.hasOwnProperty.call(partial, CADENCE_KEYS[i])) return true;
  }
  return false;
}

// Keeps recurrence.graceInterval/graceUnit consistent with whatever cadence `merged` now
// describes. Lives here rather than inside EarlySubmissionEditor because this only ever runs
// as part of an already real, already dirty-worthy cadence edit -- never merely from rendering.
// `previousUnit` is the grace unit the rule had before the edit.
function reconcileGrace(merged, previousUnit) {
  var seed = seedGraceFor(merged);
  // Not customized: always follow the cadence's own default (within its own ceiling by
  // construction, so this reseed is never a destructive rewrite of anything the user chose).
  //
  // A customized value is also reseeded when the edit FLIPS the unit (days <-> hours, which one
  // click of the Due toggle does). The stored number was chosen against the old unit and means
  // something else under the new one: a 4-hour window re-labelled "4 days" on a daily rule
  // exceeds that rule's 1-day ceiling and the save fails, and a 3-day window re-labelled
  // "3 hours" silently shrinks the window by a factor of eight. The suggestion for the new
  // cadence is the only value that is meaningful in the new unit, so the grace goes back to
  // following it (graceIsCustom false) and the Use Suggested link disappears with it.
  //
  // An explicit "no grace" (null, or a typed 0) is exempt: it is as deliberate as typing a
  // number, it is valid under every ceiling, and it means the same thing in either unit -- 0
  // days is 0 hours. Only its unit is re-stamped so the pair the API validator checks still
  // agrees. EarlySubmissionEditor emits numbers via parseNonNegativeInt, never the string "0".
  var noGrace = merged.graceInterval === null || merged.graceInterval === 0;
  if (!merged.graceIsCustom || (previousUnit !== seed.unit && !noGrace)) {
    merged.graceInterval = seed.value;
    merged.graceUnit = seed.unit;
    merged.graceIsCustom = false;
    return;
  }
  // Same unit: the user's own number stands. The ceiling is deliberately NOT enforced here.
  // CadenceEditor emits a real interval on every keystroke of a multi-digit number -- typing
  // "10" emits interval: 1, then interval: 10 -- and there is no way to tell that transient 1
  // apart from a deliberately chosen 1. Clamping a custom grace against a ceiling computed from
  // it destroys a valid value before the real one ever lands (weekly x10 / 30-day grace: the
  // first keystroke of "10" clamps 30 down to one week's ceiling of 7, and the second
  // keystroke's min(7, 70) can never recover it -- the 30 is simply gone). An over-ceiling custom grace is
  // instead left for the existing validation path to report as a visible error (the API's
  // validator, surfaced through errors.scheduledCompletionGraceInterval) rather than a value
  // silently rewritten out from under the user.
  merged.graceUnit = seed.unit;
}

// The fields that decide how a picked first due date is resolved: the rule's coarseness
// (frequency plus hoursOfDay) and the zone it is read in.
// The partial keys that can move a stored calendar date: coarseness (frequency, hoursOfDay)
// re-stores the first due date; timeZone re-stores both it and the yearly start date.
var ANCHOR_KEYS = ['frequency', 'hoursOfDay', 'timeZone'];

function touchesAnchoring(partial) {
  for (var i = 0; i < ANCHOR_KEYS.length; i++) {
    if (Object.prototype.hasOwnProperty.call(partial, ANCHOR_KEYS[i])) return true;
  }
  return false;
}

// Re-resolves the rule's stored calendar dates onto the same calendar day under the edited rule.
// Both dates are read in the zone they were picked in (the rule's zone before this edit), so a
// zone change moves the instant without moving the day.
//
// - startDate: the day a yearly rule recurs on, read by the engine in the rule's zone. Only a
//   zone change moves it; the yearly month/day selectors would otherwise flip to the previous
//   day when the new zone is west of the old one.
// - firstDueDate: re-stored with exactly the rule FirstDueDateEditor.handleDateChange applies at
//   pick time: noon when the rule is day-coarse, the day's start when it names times of day
//   (read that comment for why noon would make the engine skip every named hour before it).
//   Without this, a date picked under "By End of Day" stays stored at noon after a switch to
//   "At a Set Time", so the first occurrence lands a day late while the picker still shows the
//   day the user chose.
function reanchorCalendarDates(merged, previousTimeZone) {
  var zone = merged.timeZone || getBrowserTimeZone();
  var pickedIn = previousTimeZone || zone;
  if (pickedIn !== zone && typeof merged.startDate === 'number') {
    merged.startDate = getZonedTimestamp(getZonedCalendarDate(merged.startDate, pickedIn), zone, 0, 0, 0);
  }
  if (merged.firstDueAnchor !== 'date') return;
  if (typeof merged.firstDueDate !== 'number') return;
  var day = getZonedCalendarDate(merged.firstDueDate, pickedIn);
  merged.firstDueDate = isDayCoarse(effectiveHoursOfDay(merged.frequency, merged.hoursOfDay))
    ? getZonedTimestamp(day, zone)
    : getZonedTimestamp(day, zone, 0, 0, 0);
}

var Recurrence = function (props) {
  var config = props.config;
  var settingsFields = props.settingsFields;
  var onChange = props.onChange;
  var errors = props.errors || {};
  var t = useTranslation().t;
  var recurrence = config.recurrence || {};
  var mode = recurrence.mode || 'none';
  var status = props.scheduleStatus || null;
  var hasAssetsWithDueDates = !!(status && status.withDueDateCount > 0);
  var recompute = !!props.recomputeExistingDueDates;
  var onRecomputeChange = props.onRecomputeChange || function () {};

  var handleModeChange = function (newMode) {
    // Carry the rest of the rule across the switch. Rebuilding the object from scratch meant a
    // trip through None or Manual silently discarded the stored schedule, so coming back to
    // Scheduled rebuilt it from the defaults below -- a Mon/Wed/Fri rule returned as Mon-only,
    // no warning and no user action, and a save then persisted the narrower cadence. It read as
    // a single lost field only because every other default (weekly, interval 1, the browser
    // zone, anchor today) happens to match a typical stored rule; daysOfWeek was just the one
    // field whose real value differed from its default.
    // The per-field `|| default` fallbacks below already describe themselves as "default only
    // when there isn't one yet" -- this is what makes that true after a round trip rather than
    // only on a first switch. Scheduled fields carried while in None/Manual are inert:
    // inspectionPatchHelper gates every one of them on isScheduled, so none is ever sent for a
    // non-scheduled rule.
    var newRecurrence = Object.assign({}, recurrence, { mode: newMode });
    if (newMode === 'manual') {
      newRecurrence.fieldKey = recurrence.fieldKey || null;
      newRecurrence.fieldTitle = recurrence.fieldTitle || '';
    }
    if (newMode === 'schedule') {
      newRecurrence.frequency = recurrence.frequency || 'weekly';
      newRecurrence.interval = recurrence.interval || 1;
      newRecurrence.daysOfWeek = recurrence.daysOfWeek || ['mon'];
      newRecurrence.hoursOfDay = recurrence.hoursOfDay || [];
      newRecurrence.daysOfMonth = recurrence.daysOfMonth || [];
      // The start date is the rule's origin. It is only editable through the
      // yearly month and day selectors; every other frequency just needs one.
      // Local midnight, not the current instant, so an empty hoursOfDay really
      // means "due at midnight" as the hint tells the user.
      newRecurrence.startDate = recurrence.startDate || getStartOfDayTimestamp(new Date());
      // Keep a stored zone (re-clicking Scheduled on an existing rule must not
      // silently swap it for the browser's), default to the browser zone only
      // when there isn't one yet -- same pattern as the sibling fields above.
      newRecurrence.timeZone = recurrence.timeZone || getBrowserTimeZone();
      newRecurrence.firstDueAnchor = recurrence.firstDueAnchor || 'today';
      newRecurrence.firstDueDate = recurrence.firstDueDate || null;
      newRecurrence.firstDueFieldKey = recurrence.firstDueFieldKey || null;
      newRecurrence.firstDueFieldTitle = recurrence.firstDueFieldTitle || '';
      newRecurrence.firstDueOffsetInterval =
        recurrence.firstDueOffsetInterval === undefined ? null : recurrence.firstDueOffsetInterval;
      newRecurrence.firstDueOffsetUnit = recurrence.firstDueOffsetUnit || 'days';
      // Grace follows the same "keep what's there, default only when there is
      // nothing yet" rule as timeZone above: re-clicking Scheduled on a rule
      // that already has a grace value (custom or previously seeded) must not
      // reset it. EarlySubmissionEditor derives and displays a live default itself
      // while graceInterval is still null (it never persists that display-only
      // value), so leaving it null here is enough.
      newRecurrence.graceInterval =
        recurrence.graceInterval === undefined || recurrence.graceInterval === null
          ? null
          : recurrence.graceInterval;
      newRecurrence.graceUnit = recurrence.graceUnit || null;
      newRecurrence.graceIsCustom = !!recurrence.graceIsCustom;
    }
    onChange('recurrence', newRecurrence);
  };

  // Every schedule sub-editor emits a partial recurrence; this merges it. A partial that touches
  // the cadence (CADENCE_KEYS -- CadenceEditor is the only source of these)
  // also keeps the grace value consistent with it, riding on an edit that is already real and
  // already dirty-worthy, so it adds no new dirty-marking call of its own (contrast
  // EarlySubmissionEditor's old mount-effect approach, review round 1, Important #2).
  var handleRecurrencePartial = function (partial) {
    var merged = Object.assign({}, recurrence, partial);
    if (touchesCadence(partial)) {
      reconcileGrace(merged, graceUnitFor(effectiveHoursOfDay(recurrence.frequency, recurrence.hoursOfDay)));
    }
    if (touchesAnchoring(partial)) reanchorCalendarDates(merged, recurrence.timeZone);
    onChange('recurrence', merged);
  };

  var handleFieldChange = function (suuid, title) {
    onChange('recurrence', Object.assign({}, recurrence, {
      fieldKey: suuid,
      fieldTitle: title,
    }));
  };

  var modeLabels = {
    none: t('inspect:recurrenceNone'),
    manual: t('inspect:recurrenceManual'),
    schedule: t('inspect:recurrenceScheduled'),
  };

  var modeDescriptions = {
    none: t('inspect:recurrenceNoneHint'),
    manual: t('inspect:recurrenceManualHint'),
    schedule: t('inspect:recurrenceScheduleHint'),
  };

  return (
    <div className="inspect-section-recurrence">
      <div className="inspect-settings-content-header">
        <h3 className="inspect-settings-content-title">{t('inspect:sectionRecurrence')}</h3>
        <p className="inspect-settings-content-description">{t('inspect:recurrenceDescription')}</p>
      </div>

      <SegmentedToggle
        options={MODES.map(function (m) {
          return { value: m, label: modeLabels[m] };
        })}
        value={mode}
        onChange={handleModeChange}
        ariaLabel={t('inspect:sectionRecurrence')}
        hasError={!!errors.recurrenceType}
      />
      <p className="inspect-mode-hint">{modeDescriptions[mode]}</p>
      {errors.recurrenceType && (
        <div className="slate-text-input-error">{errors.recurrenceType}</div>
      )}

      {mode === 'manual' && (
        <div className="inspect-recurrence-field">
          <label className="inspect-recurrence-label">{t('inspect:recurrenceFieldLabel')}</label>
          <FieldSelector
            currentSUUID={recurrence.fieldKey || ''}
            fields={scopeToAssetFields(settingsFields)}
            onChange={handleFieldChange}
            filterType="dateTime"
            error={!!errors.manualRecurrenceDateFieldPathHash}
          />
          {errors.manualRecurrenceDateFieldPathHash && (
            <div className="slate-text-input-error">{errors.manualRecurrenceDateFieldPathHash}</div>
          )}
        </div>
      )}

      {mode === 'schedule' && (
        <div className="inspect-recurrence-schedule">
          <div className="inspect-recurrence-group">
            <h4 className="inspect-recurrence-group-title">{t('inspect:scheduleGroupTitle')}</h4>
            <CadenceEditor
              frequency={recurrence.frequency}
              interval={recurrence.interval}
              daysOfWeek={recurrence.daysOfWeek}
              hoursOfDay={recurrence.hoursOfDay}
              daysOfMonth={recurrence.daysOfMonth}
              startDate={recurrence.startDate}
              timeZone={recurrence.timeZone}
              onChange={handleRecurrencePartial}
              t={t}
              errors={errors}
            />
            <EarlySubmissionEditor
              frequency={recurrence.frequency}
              interval={recurrence.interval}
              hoursOfDay={recurrence.hoursOfDay}
              daysOfWeek={recurrence.daysOfWeek}
              daysOfMonth={recurrence.daysOfMonth}
              graceInterval={recurrence.graceInterval}
              graceIsCustom={recurrence.graceIsCustom}
              onChange={handleRecurrencePartial}
              t={t}
              errors={errors}
            />
            <TimeZoneEditor
              timeZone={recurrence.timeZone}
              onChange={handleRecurrencePartial}
              t={t}
              errors={errors}
            />
          </div>

          <div className="inspect-recurrence-group">
            <div className="inspect-recurrence-group-head">
              <h4 className="inspect-recurrence-group-title">{t('inspect:assetsGroupTitle')}</h4>
              {!!status && (
                <span className="inspect-recurrence-group-count">
                  {t('inspect:assetsCount', { total: status.templateCount, withDueDate: status.withDueDateCount })}
                </span>
              )}
            </div>
            <FirstDueDateEditor
              anchor={recurrence.firstDueAnchor}
              date={recurrence.firstDueDate}
              fieldKey={recurrence.firstDueFieldKey}
              offsetInterval={recurrence.firstDueOffsetInterval}
              offsetUnit={recurrence.firstDueOffsetUnit}
              settingsFields={settingsFields}
              timeZone={recurrence.timeZone}
              frequency={recurrence.frequency}
              hoursOfDay={recurrence.hoursOfDay}
              onChange={handleRecurrencePartial}
              t={t}
              errors={errors}
            />
            {hasAssetsWithDueDates && (
              <div className="inspect-recurrence-grid-row">
                <span className="inspect-recurrence-grid-label">{t('inspect:onSaveLabel')}</span>
                <div className="inspect-recurrence-grid-control">
                  <SegmentedToggle
                    inline
                    options={[
                      { value: false, label: t('inspect:existingDueDatesKeep') },
                      { value: true, label: t('inspect:existingDueDatesRecomputeAll', { count: status.templateCount }) },
                    ]}
                    value={recompute}
                    onChange={onRecomputeChange}
                    ariaLabel={t('inspect:onSaveLabel')}
                  />
                  <p className="inspect-mode-hint inspect-recurrence-grid-hint">{t('inspect:onSaveHint')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Recurrence.propTypes = {
  config: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  settingsFields: PropTypes.array.isRequired,
  scheduleStatus: PropTypes.object,
  recomputeExistingDueDates: PropTypes.bool,
  onRecomputeChange: PropTypes.func,
  errors: PropTypes.object,
};

export default Recurrence;
